import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import ReactDOM from 'react-dom';
import {
  IconCustomer2,
  IconFinance,
  IconMail,
  IconMarketplace,
  IconOrders,
  IconSearch,
  IconSettings,
  IconStats,
  IconWarehouse2,
  FailedOrder,
} from '../../../assets/svg/icons';
import routes from '../../../setup/consts/routes';
import {
  StyledPaletteBackdrop,
  StyledPaletteEmpty,
  StyledPaletteFooter,
  StyledPaletteFooterGroup,
  StyledPaletteGroup,
  StyledPaletteHint,
  StyledPaletteInput,
  StyledPaletteInputRow,
  StyledPaletteItem,
  StyledPaletteItemBody,
  StyledPaletteItemMeta,
  StyledPaletteItemTitle,
  StyledPaletteList,
  StyledPalettePanel,
} from './styles';

type Command = {
  id: string;
  group: 'Navigate' | 'Create' | 'Help';
  title: string;
  meta?: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  keywords?: string[];
  run: (history: ReturnType<typeof useHistory>) => void;
};

const COMMANDS: Command[] = [
  {
    id: 'nav-dashboard',
    group: 'Navigate',
    title: 'Dashboard',
    meta: 'Home — what needs attention',
    icon: IconStats,
    keywords: ['home', 'overview', 'kpi', 'metrics'],
    run: (h) => h.push(routes.DASHBOARD),
  },
  {
    id: 'nav-warehouse',
    group: 'Navigate',
    title: 'Warehouse',
    meta: 'Browse and manage products',
    icon: IconWarehouse2,
    keywords: ['stock', 'inventory', 'products'],
    run: (h) => h.push(routes.WAREHOUSE),
  },
  {
    id: 'nav-orders',
    group: 'Navigate',
    title: 'Orders',
    meta: 'Track and process orders',
    icon: IconOrders,
    keywords: ['shipments', 'deliveries'],
    run: (h) => h.push(routes.ORDERS),
  },
  {
    id: 'nav-failed-orders',
    group: 'Navigate',
    title: 'Failed Orders',
    meta: 'Orders that need attention',
    icon: FailedOrder,
    keywords: ['errors', 'returns', 'issues'],
    run: (h) => h.push(routes.FAILED_ORDERS),
  },
  {
    id: 'nav-financials',
    group: 'Navigate',
    title: 'Financials',
    meta: 'Revenue, fees and settlements',
    icon: IconFinance,
    keywords: ['money', 'reports', 'revenue', 'invoice'],
    run: (h) => h.push(routes.FINANCIALS),
  },
  {
    id: 'nav-customers',
    group: 'Navigate',
    title: 'Customers',
    meta: 'View customer history',
    icon: IconCustomer2,
    keywords: ['clients', 'people'],
    run: (h) => h.push(routes.CUSTOMERS),
  },
  {
    id: 'nav-marketplace',
    group: 'Navigate',
    title: 'Marketplace',
    meta: 'Discover products and brands',
    icon: IconMarketplace,
    keywords: ['shop', 'discover', 'browse'],
    run: (h) => h.push(routes.MARKETPLACE),
  },
  {
    id: 'nav-contact',
    group: 'Help',
    title: 'Contact Us',
    meta: 'Reach Sandoog support',
    icon: IconMail,
    keywords: ['support', 'help'],
    run: (h) => h.push(routes.CONTACT_US),
  },
  {
    id: 'nav-settings',
    group: 'Navigate',
    title: 'Settings',
    meta: 'Account, billing, integrations',
    icon: IconSettings,
    keywords: ['profile', 'config', 'preferences'],
    run: (h) => h.push(routes.ACCOUNT_DETAILS),
  },
  {
    id: 'create-order',
    group: 'Create',
    title: 'Place a new order',
    meta: 'Start the order entry flow',
    icon: IconOrders,
    keywords: ['new', 'order', 'add', 'place'],
    run: (h) => h.push(routes.PLACE_ORDER),
  },
  {
    id: 'create-product',
    group: 'Create',
    title: 'Add a product',
    meta: 'Create a new warehouse item',
    icon: IconWarehouse2,
    keywords: ['new', 'product', 'sku', 'add'],
    run: (h) => h.push(`${routes.WAREHOUSE}/product`),
  },
];

const score = (cmd: Command, query: string) => {
  if (!query) return 1;
  const q = query.toLowerCase();
  const haystack = [
    cmd.title,
    cmd.meta ?? '',
    ...(cmd.keywords ?? []),
    cmd.group,
  ]
    .join(' ')
    .toLowerCase();
  if (cmd.title.toLowerCase().startsWith(q)) return 100;
  if (haystack.includes(q)) return 10;
  // simple subsequence
  let qi = 0;
  for (let i = 0; i < haystack.length && qi < q.length; i += 1) {
    if (haystack[i] === q[qi]) qi += 1;
  }
  return qi === q.length ? 1 : 0;
};

const CommandPalette: React.FC = () => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global hotkey: ⌘K / Ctrl+K toggles. Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isOpenCombo =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isOpenCombo) {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      // delay one tick so the input is mounted
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const ranked = useMemo(() => {
    const withScore = COMMANDS.map((cmd) => ({ cmd, s: score(cmd, query) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.cmd);
    return withScore;
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map<Command['group'], Command[]>();
    ranked.forEach((cmd) => {
      const arr = map.get(cmd.group) ?? [];
      arr.push(cmd);
      map.set(cmd.group, arr);
    });
    return Array.from(map.entries());
  }, [ranked]);

  const close = useCallback(() => setOpen(false), []);

  const runAt = useCallback(
    (idx: number) => {
      const cmd = ranked[idx];
      if (!cmd) return;
      close();
      cmd.run(history);
    },
    [ranked, history, close]
  );

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, ranked.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runAt(selectedIdx);
    }
  };

  if (!open) return null;

  const palette = (
    <StyledPaletteBackdrop
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <StyledPalettePanel role='dialog' aria-label='Command palette'>
        <StyledPaletteInputRow>
          <IconSearch />
          <StyledPaletteInput
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIdx(0);
            }}
            onKeyDown={onInputKey}
            placeholder='Type a command or search…'
            autoComplete='off'
            spellCheck={false}
          />
          <StyledPaletteHint>ESC</StyledPaletteHint>
        </StyledPaletteInputRow>

        <StyledPaletteList>
          {grouped.length === 0 ? (
            <StyledPaletteEmpty>No matches for “{query}”.</StyledPaletteEmpty>
          ) : (
            grouped.map(([group, items]) => (
              <React.Fragment key={group}>
                <StyledPaletteGroup>{group}</StyledPaletteGroup>
                {items.map((cmd) => {
                  const idxInRanked = ranked.indexOf(cmd);
                  const Icon = cmd.icon;
                  return (
                    <StyledPaletteItem
                      key={cmd.id}
                      active={idxInRanked === selectedIdx}
                      onMouseEnter={() => setSelectedIdx(idxInRanked)}
                      onClick={() => runAt(idxInRanked)}
                    >
                      <Icon />
                      <StyledPaletteItemBody>
                        <StyledPaletteItemTitle>
                          {cmd.title}
                        </StyledPaletteItemTitle>
                        {cmd.meta ? (
                          <StyledPaletteItemMeta>
                            {cmd.meta}
                          </StyledPaletteItemMeta>
                        ) : null}
                      </StyledPaletteItemBody>
                    </StyledPaletteItem>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </StyledPaletteList>

        <StyledPaletteFooter>
          <StyledPaletteFooterGroup>
            <StyledPaletteHint>↑</StyledPaletteHint>
            <StyledPaletteHint>↓</StyledPaletteHint>
            <span>navigate</span>
          </StyledPaletteFooterGroup>
          <StyledPaletteFooterGroup>
            <StyledPaletteHint>↵</StyledPaletteHint>
            <span>open</span>
          </StyledPaletteFooterGroup>
          <StyledPaletteFooterGroup>
            <StyledPaletteHint>⌘K</StyledPaletteHint>
            <span>toggle</span>
          </StyledPaletteFooterGroup>
        </StyledPaletteFooter>
      </StyledPalettePanel>
    </StyledPaletteBackdrop>
  );

  return ReactDOM.createPortal(palette, document.body);
};

export default CommandPalette;
